/**
 * Central registry of license families surfaced by the ticket License Type
 * picker (the `license-picker` additional-field type).
 *
 * A "license family" maps a template field's `licensePicker.catalogCategory`
 * to the product-catalog SKU prefix(es) that identify its licenses. Every
 * consumer — the picker itself, license proration pricing, the renewal-month
 * derivation, and the admin config dropdown — resolves SKUs through this one
 * table, so behaviour can never drift between them again.
 *
 * ─── Adding a new family (e.g. ESET, Bitdefender, Dropsuite) ───
 * Add one entry below with its `catalogCategory` key, a human `label`, and the
 * SKU prefix(es) that family uses in the Dataverse product catalog. Nothing
 * else needs to change: the picker, pricing, and admin dropdown all pick it up.
 * (Confirm the real prefix against the catalog — all Microsoft licensing SKUs
 * start with `BML`, all Google licensing SKUs with `BGL`. This is broad: every
 * `BML.*`/`BGL.*` SKU — including non-seat add-ons like `BGL.AddStorage` — counts
 * as that family.)
 */

export interface LicenseCategoryDef {
  /** Value stored in a template field's `licensePicker.catalogCategory`. */
  readonly catalogCategory: string;
  /** Human-readable label for admin dropdowns. */
  readonly label: string;
  /**
   * SKU prefixes that identify this family in the product catalog and the
   * customer renewal schedule. A SKU belongs to the family when it starts
   * with any of these.
   */
  readonly skuPrefixes: readonly string[];
}

export const LICENSE_CATEGORIES = [
  { catalogCategory: 'licensing-m365', label: 'Microsoft 365', skuPrefixes: ['BML'] },
  { catalogCategory: 'licensing-google', label: 'Google Workspace', skuPrefixes: ['BGL'] },
  // Future: ESET, Bitdefender, Dropsuite — add one line each (verify SKU prefix).
] as const satisfies readonly LicenseCategoryDef[];

/** Union of valid `catalogCategory` values, derived from the registry above. */
export type LicenseCatalogCategory = (typeof LICENSE_CATEGORIES)[number]['catalogCategory'];

const BY_CATEGORY: Record<string, LicenseCategoryDef> = Object.fromEntries(
  LICENSE_CATEGORIES.map((c) => [c.catalogCategory, c]),
);

/** True when `sku` belongs to the given license category. */
export function skuMatchesCategory(sku: string, category: LicenseCatalogCategory): boolean {
  const def = BY_CATEGORY[category];
  return def ? def.skuPrefixes.some((p) => sku.startsWith(p)) : false;
}

/**
 * The license family a SKU belongs to, or null if the SKU isn't a recognised
 * license. Use this to find sibling licenses (same family) in the renewal
 * schedule without hardcoding prefixes at the call site.
 */
export function getLicenseCategoryForSku(sku: string): LicenseCategoryDef | null {
  return LICENSE_CATEGORIES.find((c) => c.skuPrefixes.some((p) => sku.startsWith(p))) ?? null;
}

/** `{ value, label }` options for admin category dropdowns. */
export const LICENSE_CATEGORY_OPTIONS: ReadonlyArray<{ value: LicenseCatalogCategory; label: string }> =
  LICENSE_CATEGORIES.map((c) => ({ value: c.catalogCategory, label: c.label }));
